import SearchIcon from '@mui/icons-material/Search'
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import Button from '@mui/material/Button'
import dayjs from 'dayjs'
import { useCallback, useState } from 'react'

import ClearIcon from '@mui/icons-material/Clear'
import DateRangePicker from '../../../../../components/DateRangePicker'
import CategorySelectSearch from '../../../../../components/SelectSearch/CategorySelectSearch'
import ChannelSelectSearch from '../../../../../components/SelectSearch/ChannelSelectSearch'
import ProductGroupSelectSearch from '../../../../../components/SelectSearch/ProductGroupSelectSearch'
import ProductSelectSearch from '../../../../../components/SelectSearch/ProductSelectSearch'
import useAxios from '../../../../../hooks/useAxios'
import { ICategory } from '../../../../../types/category'
import { IDistributionChannel } from '../../../../../types/distribution-channel'
import { PaginationResponseInterface } from '../../../../../types/pagination'
import { IProduct } from '../../../../../types/product'
import { EStatusOrderShowTracking } from '../../../../Order/components/OrderFilterDrawer'
import UserSelectSearch from '../../../../../components/SelectSearch/UserSelectSearch'

const menuItemsTypeStatus = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'เฉพาะรายการที่มี Tracking',
    value: EStatusOrderShowTracking.TRACKING,
  },
  {
    label: 'ยังไม่มี Tracking',
    value: EStatusOrderShowTracking.NO_TRACKING,
  },
  {
    label: 'สินค้าค้างส่ง',
    value: EStatusOrderShowTracking.PENDING,
  },
]

export interface IFilterMapped {
  startDate?: string
  endDate?: string
  categoryObjectIds?: string[]
  productObjectIds?: string[]
  productGroups?: string[]
  distributionObjectIds?: string[]
  trackingStatus?: string | any
  commissionUsername?: string
  dateType?: string
}

interface FilterGroupProps {
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
}

const DailyOrdersReportFilter = ({
  filter,
  onFilterChange,
  onSearch,
}: FilterGroupProps) => {
  const {
    startDate,
    endDate,
    categoryObjectIds,
    productObjectIds,
    distributionObjectIds,
  } = filter

  const [searchProduct, setSearchProduct] = useState<string>('')
  const [searchCategory, setSearchCategory] = useState<string>('')
  const [searchDistributionChannel, setSearchDistributionChannel] =
    useState<string>('')
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')

  const productsList = useAxios<PaginationResponseInterface<IProduct>>({
    method: 'GET',
    url: '/product',
    params: {
      page: 1,
      perPage: 5,
      search: searchProduct,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchProduct = async (value: string) => {
    setSearchProduct(value)
    await productsList.mutate()
  }

  const categoriesList = useAxios<PaginationResponseInterface<ICategory>>({
    method: 'GET',
    url: '/category',
    params: {
      page: 1,
      perPage: 5,
      search: searchCategory,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchCategory = async (value: string) => {
    setSearchCategory(value)
    await categoriesList.mutate()
  }

  const distributionChannelsList = useAxios<
    PaginationResponseInterface<IDistributionChannel>
  >({
    method: 'GET',
    url: '/distribution-channels',
    params: {
      page: 1,
      perPage: 5,
      search: searchDistributionChannel,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchDistributionChannel = async (value: string) => {
    setSearchDistributionChannel(value)
    try {
      await distributionChannelsList.mutate()
    } catch (error) {}
  }

  const handleFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      onFilterChange({ ...filter, ...newFilter })
    },
    [filter, onFilterChange],
  )

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={12} lg={2}>
        <TextField
          label="ประเภทวันที่"
          fullWidth
          value={filter.dateType || 'orderDate'}
          onChange={(e) =>
            onFilterChange({ ...filter, dateType: e.target.value })
          }
          select
        >
          <MenuItem value="orderDate">วันที่คำสั่งซื้อ</MenuItem>
          <MenuItem value="items.orderDate">วันที่สินค้า</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} lg={4}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={(startDate, endDate) => {
            onFilterChange({ ...filter, startDate, endDate })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <ProductSelectSearch
          resetAutoComplete={resetAutoComplete}
          products={productObjectIds}
          handleFilterChange={handleFilterChange}
          onSelect={(e) => {
            const selectedProductIds = e?.map(
              (product: any) => product?.value?.objectId,
            )
            handleFilterChange({ productObjectIds: selectedProductIds })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <ProductGroupSelectSearch
          resetAutoComplete={resetAutoComplete}
          productGroups={filter.productGroups}
          onSelect={(e) => {
            const selectedGroupIds = e?.map((group) => group.value.objectId)
            handleFilterChange({ productGroups: selectedGroupIds })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <CategorySelectSearch
          resetAutoComplete={resetAutoComplete}
          categories={categoryObjectIds}
          onSelect={(e) => {
            const selectedCategoryIds = e?.map(
              (category: any) => category?.value?.objectId,
            )
            onFilterChange({
              ...filter,
              categoryObjectIds: selectedCategoryIds,
            })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <ChannelSelectSearch
          resetAutoComplete={resetAutoComplete}
          label={'เลือกช่องทางการขาย'}
          onSelect={(e) => {
            const selectedChannelIds = e?.map(
              (channel: any) => channel?.value?.objectId,
            )
            onFilterChange({
              ...filter,
              distributionObjectIds: selectedChannelIds,
            })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <UserSelectSearch
          resetAutoComplete={resetAutoComplete}
          label={'ผู้ขาย'}
          onSelect={(e) =>
            onFilterChange({ ...filter, commissionUsername: e?.value.username })
          }
          name="commissionUsername"
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="statusOrderShowTracking-label">สถานะ</InputLabel>
          <Select
            id="statusOrderShowTracking"
            name="statusOrderShowTracking"
            labelId="statusOrderShowTracking-label"
            fullWidth
            value={filter.trackingStatus || 'all'}
            onChange={(e) => {
              onFilterChange({ ...filter, trackingStatus: e.target.value })
            }}
          >
            {menuItemsTypeStatus.map((item, index: number) => (
              <MenuItem value={item.value} key={index}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={3}>
        <Button
          color="primary"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => onSearch(filter)}
          sx={{ height: '100%' }}
        >
          ค้นหา
        </Button>
      </Grid>
      <Grid item xs={4} lg={2}>
        <Button
          color="inherit"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<ClearIcon />}
          onClick={() => {
            onFilterChange({
              categoryObjectIds: undefined,
              distributionObjectIds: undefined,
              productObjectIds: undefined,
              productGroups: undefined,
              startDate: dayjs().subtract(30, 'days').format(),
              endDate: dayjs().format(),
            })
            onSearch({
              categoryObjectIds: undefined,
              distributionObjectIds: undefined,
              productObjectIds: undefined,
              productGroups: undefined,
              startDate: dayjs().subtract(30, 'days').format(),
              endDate: dayjs().format(),
            })
            setResetAutoComplete(new Date().getTime().toString())
          }}
          sx={{ height: '100%' }}
        >
          รีเซ็ต
        </Button>
      </Grid>
    </Grid>
  )
}

export default DailyOrdersReportFilter
